import parsePhoneNumber from 'libphonenumber-js';
import { AsYouType } from 'libphonenumber-js';
import 'libphonenumber-js/min';

const plausiblePhone = /^\+?[0-9()-\s]*$/;
const ignoredCharacters = /[()-\s]/g;
const cleanPhone = (string: string) => string.replace(ignoredCharacters, '');

function forceInternational(phone: string, assumeUS = false) {
  phone = cleanPhone(phone.trim());
  if (!phone.startsWith('+')) {
    if (!phone.startsWith('1') && assumeUS) {
      return `+1${phone}`;
    }
    return `+${phone}`;
  }
  return phone;
}

// Once the input is done, we can also check overall length for NANP numbers.
export function isValidInternationalPhoneNumber(
  phone: string | null | undefined,
): boolean {
  return !!parsePhoneNumber(phone || '', 'US')?.isValid();
}

// Format a number as e.g. +44525285822, for Twilio
export function formatInternationalPhoneNumberE164(
  phone: string | undefined | null,
): string | null {
  if (!phone) return null;
  phone = forceInternational(phone);
  const number = parsePhoneNumber(phone);
  return number?.number as string;
}

// Format a number as e.g. +1 (412) 123-4567 or +44 52 528 5822, for human
// consumption and handle cases where the user is still mid-input, and so the
// number is incomplete, or they may be deleting or editing in addition to
// adding to the end.
export function formatPartialInternationalPhoneNumberHuman(
  phone: string,
): string {
  // if only a couple characters have been typed, do nothing; let the user have
  // complete control for a sec.
  if (phone.length <= 3) return phone;

  // If the input is nonsense -- not a phone number -- just leave it alone and
  // let the user sort themselves out
  if (!cleanPhone(phone).match(plausiblePhone)) {
    return phone;
  }
  phone = forceInternational(phone, true);

  // The number is explicitly international-US and starts with +1
  if (phone.startsWith('+1')) {
    return formatPartialNANPNumberHuman(phone);
  }

  const number = new AsYouType();
  return number.input(phone);
}

export function formatInternationalPhoneNumberHuman(phone: string): string {
  const cleanedPhone = forceInternational(phone);
  const number = parsePhoneNumber(cleanedPhone, 'US');
  if (number?.country === 'US') {
    // In the future, formatNational or formatInternational; this
    // oops-all-dashes format is for temporary backwards compatibility
    return formatPartialNANPNumberHuman(number.number);
  } else if (number?.isValid()) {
    return number?.formatInternational();
  }
  // Internal services on sandbox and QA produce sample data that includes
  // invalid US phone numbers. Treat invalid numbers that look USish as US
  // numbers. Real numbers in prod should never hit this branch.
  if (cleanedPhone.length === 12 && cleanedPhone.startsWith('+1')) {
    return formatPartialNANPNumberHuman(phone);
  }
  return phone;
}

// Format a NANP number like +1 (123) 456-7890. The only subtlety here is that
// the number may be partially finished, and the user might be adding or
// deleting; so hang one character behind -- wait until you're a number *past* a
// block to format it.
//
// Libphonenumber-js has a demo that implies it does this, but the latest
// version does not; it formats US numbers as 1 234 567 8900.
function formatPartialNANPNumberHuman(phone: string): string {
  if (phone.length < 4) return phone;

  phone = phone.substring(2);
  let formattedNumber = phone;
  if (phone.length >= 7) {
    formattedNumber = `${phone.substring(0, 3)}-${phone.substring(3, 6)}-${phone.substring(6)}`;
  } else if (phone.length > 3) {
    /* applies formatting after area code and prefix */
    formattedNumber = `${phone.substring(0, 3)}-${phone.substring(3)}`;
  }
  return `1-${formattedNumber}`;
}
